// Internationalization (i18n) configuration
// Simple flat key-value structure for translations

// Current language - managed dynamically from user preferences
let currentLang = 'en'; // Default to English

const translations = {
  en: {
    // Common
    'common.enabled': 'Enabled',
    'common.save': 'Save Settings',
    'common.saveSuccess': 'Configurations updated successfully!',
    'common.saveError': 'Error updating configurations. Please try again.',
    'common.loading': 'Loading configuration data...',

    // Heating Settings - Main
    'heating.title': 'Heating Settings',
    'heating.summerMode': 'Summer mode active',

    // Basic Settings
    'basic.title': 'Basic Settings',
    'basic.chooseScheme': 'Choose Scheme',
    'basic.summerMode': 'Summer mode',
    'basic.summerDecrease': 'Temperature decrease (°C)',

    // Room Table
    'room.title': 'Thermostats, devices and temperature profiles',
    'room.name': 'Thermostat',
    'room.profile': 'Temperature profile',
    'room.thermometer': 'Thermometer',
    'room.heaters': 'Heaters',

    // Time-Based Adjustments
    'timeAdj.title': 'Time-Based Adjustments',
    'timeAdj.name': 'Name',
    'timeAdj.degrees': 'Degrees (°C)',
    'timeAdj.time': 'Time',
    'timeAdj.activeDays': 'Active Days',
    'timeAdj.warning': 'Warning',
    'timeAdj.errorValueZero': 'Value set as 0',
    'timeAdj.errorMissingTime': 'Start or End time is missing',
    'timeAdj.errorInvalidTime': 'Start/End time must be between 0 and 23',
    'timeAdj.errorMissingDays': 'Active days configuration is missing or invalid',
    'timeAdj.errorNoActiveDay': 'At least one active day must be selected',

    // Profile Table
    'profile.title': 'Electricity price based room temperature profiles',
    'profile.optimisationEnabled': 'Habitaio control enabled',
    'profile.priceRanges': 'Hourly electricity price ranges [c/kWh]',
    'profile.targetTemp': 'Target temperature',
    'profile.targetTempScheme': 'Target temperature',
    'profile.timeBasedAdj': 'Time-Based Adjustments',
    'profile.enabledFor': 'Enabled for',
    'profile.reserveHeating': 'Spot price based Reserve Heating',
    'profile.reserveHeatingAfternoon': 'Spot price based Reserve Heating for Afternoons',
    'profile.hours': 'Hours',
    'profile.tempAdjustments': 'Spot price based Temperature Adjustments',
    'profile.tempChange': 'Temperature change on',
    'profile.thermostatSettings': 'Thermostat Specific Settings',
    'profile.parameter': 'Parameter',
    'profile.value': 'Value',
    'profile.pvSettings': 'Photovoltaics Settings',
    'profile.onExcessPower': 'On excess power, apply temperature for',
    'profile.reserveHeatingDesc': 'Increase the target temperature during the lowest spot price',
    'profile.reserveHeatingDesc2': 'by the',
    'profile.afternoonDesc': 'Between 12 - 17, Increase the target temperature during the lowest spot price',
    'profile.priceChangeDesc': 'Change the target temperature when spot price is above',
    'profile.by': 'by',
    'profile.summerModeNote': 'Summer mode active, {decrease}°C applied to target temperatures',
    'profile.coolingModeNote': 'Cooling mode active: {names}',
    'profile.pvHeatingTemp': 'PV excess: heating target temperature (°C)',
    'profile.pvCoolingTemp': 'PV excess, summer mode: cooling target temperature (°C)',
    'profile.mobileWarning': 'Some temperature profile features may have limited functionality on mobile devices.',
    'profile.degrees': 'Degrees (°C)',
    'profile.degreesUnit': '(°C)',

    // Thermostat Settings
    'thermostat.dynamicCalibration': 'Automatic calibration enabled',
    'thermostat.fanControl': 'Fan control enabled',
    'thermostat.calibrationValue': 'Calibration adjustment (°C)',
    'thermostat.adjustmentValue': 'Temperature adjustment (°C)',
    'thermostat.overrideEnabled': 'Override enabled',
    'thermostat.overrideMinutes': 'Override duration (minutes)',
    'thermostat.reserveHeating': 'Thermostat Specific Reserve Heating',
    'thermostat.comfortHeating': 'Thermostat Specific Comfort Heating',
    'thermostat.startHour': 'Start hour',
    'thermostat.endHour': 'End hour',
    'thermostat.priceCriteriaType': 'Price condition type',
    'thermostat.priceCriteriaRatio': 'Ratio of day average',
    'thermostat.priceCriteriaAbsolute': 'Absolute (c/kWh)',
    'thermostat.priceCriteriaRatioLabel': 'Max ratio of day avg (e.g. 0.8)',
    'thermostat.priceCriteriaAbsoluteLabel': 'Max price (c/kWh)',
    'thermostat.percentDayMax': '% of day max price',
    'thermostat.coolingMode': 'Cooling mode',
    'thermostat.coolingRequiresSummer': 'Requires summer mode to be active',
    'thermostat.coolingTemp': 'Cooling target temperature (°C)',
    'thermostat.coolingModeActive': 'Cooling mode active at {temp}°C',
    'thermostat.excludeSummerMode': 'Exclude from summer mode decrease',

    // Ventilation Settings
    'ventilation.title': 'Ventilation Settings',
    'ventilation.sensors': 'Sensors',
    'ventilation.triggerLevel': 'Trigger level for',
    'ventilation.outdoorTemp': 'Outdoor Temperature (Celsius)',
    'ventilation.co2Exceeds': 'CO2 exceeds (ppm)',
    'ventilation.humidityExceeds': 'Humidity exceeds (%)',
    'ventilation.humidityBelow': 'Humidity below (%)',
    'ventilation.co2Ppm': 'CO2 (ppm)',
    'ventilation.humidityPercent': 'Humidity (%)',
    'ventilation.triggerValue': 'Trigger value',
    'ventilation.switchBackDiff': 'Switch back difference',
    'ventilation.summerMode': 'Summer Mode',
    'ventilation.manualOverrides': 'Manual Overrides',
    'ventilation.scheduledOverrides': 'Scheduled Overrides',
    'ventilation.name': 'Name',
    'ventilation.minutes': 'Minutes',
    'ventilation.activeNow': 'Active now',
    'ventilation.active': 'Active',
    'ventilation.inactive': 'Inactive',
    'ventilation.thermometerName': 'Thermometer name',
    'ventilation.targetTemp': 'Target temperature',
    'ventilation.timeWindow': 'Time window',
    'ventilation.outdoorTempDelta': 'Outdoor temp lower by degrees',
    'ventilation.noThermometer': 'No thermometer selected',
    'ventilation.startTime': 'Start time',
    'ventilation.endTime': 'End time',
    'ventilation.invalidTimeSequence': '⚠️ Invalid time sequence for night cooling period',

    // Consumption Settings
    'consumption.title': 'Consumption Control Settings',
    'consumption.actorName': 'Actor name',
    'consumption.controlSettings': 'Control Settings',
    'consumption.activePeriod': 'Active period',
    'consumption.maxCurrent': 'Max current (A)',
    'consumption.enableHours': 'Enable for number of lowest spot price hours',
    'consumption.timeWindow': 'Time window',
    'consumption.slots': 'Number of 15min slots',
    'consumption.ranges': 'Ranges',
    'consumption.overrideSettings': 'Override Settings',
    'consumption.overrideType': 'Override type',
    'consumption.noServices': 'No consumption control services configured.',
    'consumption.missingMetadata': 'Missing metadata for consumption control configuration.',
    'consumption.notApplicable': 'N/A',

    // Monthly Status
    'monthly.previousMonth': 'Previous month',
    'monthly.nextMonth': 'Next month',
    'monthly.noData': 'No monthly data available',
    'monthly.priceComparison': 'Price Comparison',
    'monthly.pvSummary': 'Solar Power Summary - BETA',
    'monthly.heatingEnergy': 'Heating Energy Consumption',
    'monthly.heatingByDevice': 'Consumption by Heating Device',
    'monthly.heatingDistribution': 'Distribution by Heater Type',
    'monthly.heatingDeviceBreakdown': 'Energy by Heating Device',
    'monthly.consumptionDeviceBreakdown': 'Energy by Consumption Device',
    'monthly.dailyEnergyBreakdown': 'Daily Energy Breakdown',
    'monthly.energyHeating': 'Heating',
    'monthly.energyConsumption': 'Consumption Control',
    'monthly.energyOther': 'Other (Uncontrolled)',
    'monthly.totalControlled': 'Total Controlled',
    'monthly.totalMetered': 'Total Metered',
    'monthly.energySummary': 'Energy Summary',
    'monthly.totalConsumption': 'Total Consumption',
    'monthly.totalCost': 'Total Cost',
    'monthly.deviceCount': 'Devices',
    'monthly.devices': 'devices',
    'monthly.heatingSummary': 'Heating Summary',
    'monthly.consumptionSummary': 'Consumption Summary',
    'monthly.consumptionByDevice': 'Consumption by Device (kWh)',
    'monthly.costByDevice': 'Cost by Device',
    'monthly.costByDeviceEuros': 'Cost by Device (€)',
    'monthly.otherConsumption': 'Other Energy Consumption',
    'monthly.co2Levels': 'CO2 Levels',
    'monthly.temperatures': 'Temperatures',
    'monthly.humidity': 'Humidity',
    'monthly.recommendedTemps': 'Recommended room temperatures in draft-free conditions (Motiva)',
    'monthly.recommendedHumidity': 'Recommended room humidity levels:',
    'monthly.bedrooms': 'Bedrooms',
    'monthly.livingSpaces': 'Living spaces',
    'monthly.bathrooms': 'Bathrooms',
    'monthly.storageRooms': 'Storage rooms',
    'monthly.garages': 'Garages',
    'monthly.currentUtilisation': 'Current Utilisation',
    'monthly.powerTracking': 'Power Tracking',

    // Heating Charts
    'heatingChart.title': 'Electricity Price, Total Consumption and Cost',
    'heatingChart.titleWithPV': 'Electricity Price, Total Consumption and Cost with Solar Production - BETA',
    'heatingChart.thermostatDetails': 'Thermostat Details',
    'heatingChart.consumptionDetails': 'Consumption Control Details',
    'heatingChart.totalConsumption': 'Total Consumption',
    'heatingChart.totalConsumptionBelowAvg': 'Total Consumption - below day average',
    'heatingChart.totalConsumptionAboveAvg': 'Total Consumption - above day average',
    'heatingChart.electricityPrice': 'Electricity Price',
    'heatingChart.dayAverage': 'Day Average',
    'heatingChart.totalCost': 'Total Cost',
    'heatingChart.spotAvgFull': 'Average Spot Price (full 24h)',
    'heatingChart.realisedAvg': 'Realised Average Price',
    'heatingChart.estimatedSaving': 'Estimated savings, for full days only. Savings = (Total Consumption x Avg Spot Price) - Total Cost',
    'heatingChart.outdoorTempAvg': 'Average outdoor temperature',
    'heatingChart.avgTemp': 'Average temperature',
    'heatingChart.minTemp': 'Min temperature',
    'heatingChart.maxTemp': 'Max temperature',
    'heatingChart.targetTemp': 'Target Temperature',
    'heatingChart.measuredTemp': 'Measured Temperature',
    'heatingChart.consumption': 'Consumption (kWh)',
    'heatingChart.temperature': 'Temperature (°C)',
    'heatingChart.price': 'Price/Cost (c)',
    'heatingChart.energy': 'Energy (kWh)',
    'heatingChart.chargingHeatRetainer': 'Charging heat retainer',
    'heatingChart.ahpTargetTemp': 'AHP Target temperature',
    'heatingChart.calibrationValue': 'Calibration value',
    'heatingChart.fanSpeed': 'Fan speed',
    'heatingChart.outdoorTemp': 'Outdoor temperature',
    'heatingChart.settingsAt': 'Settings at',
    'heatingChart.ahpSettings': 'AHP Settings',
    'heatingChart.ahpCalibration': 'AHP Calibration',
    'heatingChart.ahpFanSpeed': 'AHP Fan Speed',
    'heatingChart.configProfile': 'Configuration profile',
    'heatingChart.output': 'Output',
    'heatingChart.override': 'Override',
    'heatingChart.pvOutput': 'PV Output',
    'heatingChart.hourlyConsumption': 'Hourly Consumption',
    'heatingChart.consumptionBelowAboveAvg': 'Consumption below avg / above avg',
    'heatingChart.gridEnergy': 'Energy from Grid / Energy to Grid',
    'heatingChart.solarProduction': 'Solar Production',
    'heatingChart.gridEnergyBelowAboveAvg': 'Grid energy below avg / above avg',
    'heatingChart.totalIncome': 'Total Income from Solar Sales',
    'heatingChart.costOfReceived': 'Cost of Grid Energy',
    'heatingChart.netCost': 'Net Cost',
    'heatingChart.gridConsumption': 'Grid Consumption',
    'heatingChart.solarConsumption': 'Solar Consumption',
    'heatingChart.energySold': 'Energy Sold',
    'heatingChart.solarSummary': 'Solar Summary',
    'heatingChart.solarOptimizationTitle': 'Solar Power Optimization - Device Control',
    'heatingChart.panelPower': 'Panel Power',
    'heatingChart.gridPower': 'Grid Power (+ import, - export)',
    'heatingChart.deviceCapacity': 'Device Capacity',
    'heatingChart.actualPanelConsumption': 'Actual Panel Consumption',
    'heatingChart.optimizationSummary': 'Optimization Summary',
    'heatingChart.producedSolarPower': 'Produced Solar Power',
    'heatingChart.consumedSolarPower': 'Consumed Solar Power',
    'heatingChart.exportSoldPower': 'Export / Sold Power',
    'heatingChart.peakSolarProduction': 'Peak Solar Production',
    'heatingChart.totalMeteredConsumption': 'Total Metered Consumption',
    'heatingChart.habitaioSmartControl': 'Habitaio Smart Control:',
    'heatingChart.phaseCurrents': 'Phase Currents (L1, L2, L3)',
    'heatingChart.totalCurrent': 'Total Current',
    'heatingChart.current': 'Current',
    'heatingChart.phase1Current': 'Phase 1 (L1)',
    'heatingChart.phase2Current': 'Phase 2 (L2)',
    'heatingChart.phase3Current': 'Phase 3 (L3)',
    'heatingChart.reference': 'Fuse',
    'heatingChart.phase1Max': 'Phase 1 Max',
    'heatingChart.phase2Max': 'Phase 2 Max',
    'heatingChart.phase3Max': 'Phase 3 Max',
    'heatingChart.phase1Avg': 'Phase 1 Avg',
    'heatingChart.phase2Avg': 'Phase 2 Avg',
    'heatingChart.phase3Avg': 'Phase 3 Avg',
    'heatingChart.totalCurrentMax': 'Total Current Max',
    'heatingChart.totalCurrentAvg': 'Total Current Avg',
    'heatingChart.phaseCurrentStats': 'Phase Current Statistics',
    'heatingChart.totalCurrentStats': 'Total Current Statistics',
    'heatingChart.meteredTotal': 'Total Household Consumption',
    'heatingChart.estimatedPhaseCurrent': 'Estimated Average Phase Current',
    'heatingChart.estimatedPhaseCurrentMax': 'Est. Avg Phase Current Max',
    'heatingChart.estimatedPhaseCurrentAvg': 'Est. Avg Phase Current Avg',
    'heatingChart.power': 'Power',
    'heatingChart.powerMax': 'Power Max',
    'heatingChart.powerAvg': 'Power Avg',
    'heatingChart.powerMin': 'Power Min',
    'heatingChart.powerConsumption': 'Power Consumption',
    'heatingChart.powerStats': 'Power Statistics',
    'heatingChart.showHourlyView': 'Show Hourly View',
    'heatingChart.show15minDetails': 'Show 15-min Details',

    // Ventilation Charts
    'ventChart.status': 'Status',
    'ventChart.history': 'History',
    'ventChart.powerChart': 'Power Chart',
    'ventChart.timeOn': 'Time on',
    'ventChart.state': 'State',
    'ventChart.threshold': 'Threshold',

    // Days of week (short form)
    'days.mon': 'Mon',
    'days.tue': 'Tue',
    'days.wed': 'Wed',
    'days.thu': 'Thu',
    'days.fri': 'Fri',
    'days.sat': 'Sat',
    'days.sun': 'Sun',

    // Days of week (full form)
    'days.monday': 'Monday',
    'days.tuesday': 'Tuesday',
    'days.wednesday': 'Wednesday',
    'days.thursday': 'Thursday',
    'days.friday': 'Friday',
    'days.saturday': 'Saturday',
    'days.sunday': 'Sunday',

    // Backend config values - Reference prices
    'backend.dayAverage': 'Day Average',
    'backend.spotAverage': 'Spot Average',
    'backend.below33': 'Below 33%',
    'backend.below50': 'Below 50%',

    // Backend config values - Common adjustment names
    'backend.nightReduction': 'Night reduction',
    'backend.dayReduction': 'Day reduction',
    'backend.weekendReduction': 'Weekend reduction',
    'backend.morningBoost': 'Morning boost',
    'backend.eveningBoost': 'Evening boost',

    // Thermostat Wizard
    'wizard.createThermostat': 'Create Thermostat',
    'wizard.addThermostat': 'Add thermostat',
    'wizard.addToExisting': 'Add to existing profile',
    'wizard.createNewProfile': 'Create new profile',
    'wizard.selectProfile': 'Select profile',
    'wizard.selectTemplate': 'Select template',
    'wizard.profileName': 'Profile name',
    'wizard.thermostatName': 'Thermostat name',
    'wizard.thermostatType': 'Type',
    'wizard.location': 'Location',
    'wizard.selectSensor': 'Temperature sensor',
    'wizard.selectHeaters': 'Heating devices',
    'wizard.advancedSettings': 'Advanced settings',
    'wizard.back': 'Back',
    'wizard.next': 'Next',
    'wizard.create': 'Create',
    'wizard.step': 'Step',
    'wizard.stepProfile': 'Profile',
    'wizard.stepDetails': 'Details',
    'wizard.stepAdvanced': 'Advanced',
    'wizard.required': 'Required',
    'wizard.optional': 'Optional',
    'wizard.selectOption': 'Select',
    'wizard.deviceSpecificSettings': 'Device-specific settings',
    'wizard.advancedSettingsHelp': 'These settings are optional and have default values.',
    'wizard.noAdvancedSettings': 'No advanced settings available.',
    'wizard.saving': 'Creating...',
    'wizard.saveSuccess': 'Thermostat created successfully!',
    'wizard.saveError': 'Failed to create thermostat. Please try again.',
  },
  fi: {
    // Common
    'common.enabled': 'Käytössä',
    'common.save': 'Tallenna asetukset',
    'common.saveSuccess': 'Asetukset päivitetty onnistuneesti!',
    'common.saveError': 'Virhe asetusten päivityksessä. Yritä uudelleen.',
    'common.loading': 'Ladataan asetuksia...',

    // Heating Settings - Main
    'heating.title': 'Lämmitysasetukset',
    'heating.summerMode': 'Kesämoodi aktiivinen',

    // Basic Settings
    'basic.title': 'Perusasetukset',
    'basic.chooseScheme': 'Valitse tila',
    'basic.summerMode': 'Kesämoodi',
    'basic.summerDecrease': 'Laske tavoitetta (°C)',

    // Room Table
    'room.title': 'Termostaatit, laitteet ja lämpötilaprofiilit',
    'room.name': 'Termostaatit',
    'room.profile': 'Lämpötilaprofiili',
    'room.thermometer': 'Lämpömittari',
    'room.heaters': 'Lämmittimet',

    // Time-Based Adjustments
    'timeAdj.title': 'Aikaperusteiset säädöt',
    'timeAdj.name': 'Nimi',
    'timeAdj.degrees': 'Asteet (°C)',
    'timeAdj.time': 'Kello',
    'timeAdj.activeDays': 'Aktiiviset päivät',
    'timeAdj.warning': 'Varoitus',
    'timeAdj.errorValueZero': 'Arvo asetettu nollaksi',
    'timeAdj.errorMissingTime': 'Alku- tai loppuaika puuttuu',
    'timeAdj.errorInvalidTime': 'Alku-/loppuajan tulee olla välillä 0-23',
    'timeAdj.errorMissingDays': 'Aktiivisten päivien määritys puuttuu tai on virheellinen',
    'timeAdj.errorNoActiveDay': 'Vähintään yhden päivän tulee olla aktiivinen',

    // Profile Table
    'profile.title': 'Sähkön hinnan mukaiset huoneiden lämpötilaprofiilit',
    'profile.optimisationEnabled': 'Habitaio-ohjaus käytössä',
    'profile.priceRanges': 'Sähkön tuntihinnan vaihteluvälit [c/kWh]',
    'profile.targetTemp': 'Tavoitelämpötila',
    'profile.targetTempScheme': 'Tavoitelämpötila,',
    'profile.timeBasedAdj': 'Aikapohjaiset säädöt',
    'profile.enabledFor': 'Käytössä tilassa',
    'profile.reserveHeating': 'Spot-hintaan perustuva varastoonlämmitys',
    'profile.reserveHeatingAfternoon': 'Spot-hintaan perustuva iltapäivän varastoonlämmitys',
    'profile.hours': 'Tunnit',
    'profile.tempAdjustments': 'Spot-hintaan perustuvat lämpötilasäädöt',
    'profile.tempChange': 'Lämpötilan muutos tilassa',
    'profile.thermostatSettings': 'Termostaattikohtaiset asetukset',
    'profile.parameter': 'Parametri',
    'profile.value': 'Arvo',
    'profile.pvSettings': 'Aurinkosähköasetukset',
    'profile.onExcessPower': 'Ylijäämäteholla, käytä lämpötilaa profiilille',
    'profile.reserveHeatingDesc': 'Nosta tavoitelämpötilaa matalimman spot-hinnan aikana',
    'profile.reserveHeatingDesc2': 'määrällä',
    'profile.afternoonDesc': 'Klo 12 - 17 välillä, nosta tavoitelämpötilaa matalimman spot-hinnan aikana',
    'profile.priceChangeDesc': 'Muuta tavoitelämpötilaa kun spot-hinta on yli',
    'profile.by': 'määrällä',
    'profile.summerModeNote': 'Kesämoodi aktiivinen, {decrease}°C tavoitelämpötiloihin',
    'profile.coolingModeNote': 'Jäähdytysmoodi aktiivinen: {names}',
    'profile.pvHeatingTemp': 'Aurinkoylijäämä: lämmitystavoite (°C)',
    'profile.pvCoolingTemp': 'Aurinkoylijäämä kesämoodissa: jäähdytystavoite (°C)',
    'profile.mobileWarning': 'Jotkut lämpötilaprofiilitoiminnot saattavat olla rajoitettuja mobiililaitteilla.',
    'profile.degrees': 'Asteet (°C)',
    'profile.degreesUnit': '(°C)',

    // Thermostat Settings
    'thermostat.dynamicCalibration': 'Automaattinen kalibrointi käytössä',
    'thermostat.fanControl': 'Puhallustehon automaattinen säätö',
    'thermostat.calibrationValue': 'Kalibrointiarvo (°C)',
    'thermostat.adjustmentValue': 'Termostaatin tavoitelämpötila profiilin asetuksesta (°C)',
    'thermostat.overrideEnabled': 'Käsiohitus sallittu',
    'thermostat.overrideMinutes': 'Käsiohituksen kesto (minuutit)',
    'thermostat.reserveHeating': 'Termostaattikohtainen varastoonlämmitys',
    'thermostat.comfortHeating': 'Termostaattikohtainen mukavuuslämmitys',
    'thermostat.startHour': 'Alkutunti',
    'thermostat.endHour': 'Lopputunti',
    'thermostat.priceCriteriaType': 'Hintaehdon tyyppi',
    'thermostat.priceCriteriaRatio': 'Osuus päivän keskihinnasta',
    'thermostat.priceCriteriaAbsolute': 'Absoluuttinen (c/kWh)',
    'thermostat.priceCriteriaRatioLabel': 'Maks. osuus keskihinnasta (esim. 0.8)',
    'thermostat.priceCriteriaAbsoluteLabel': 'Maks. spothinta (c/kWh)',
    'thermostat.percentDayMax': '% päivän maksimihinnasta',
    'thermostat.coolingMode': 'Jäähdytysmoodi',
    'thermostat.coolingRequiresSummer': 'Vaatii kesämoodin aktiiviseksi',
    'thermostat.coolingTemp': 'Jäähdytyksen tavoitelämpötila (°C)',
    'thermostat.coolingModeActive': 'Jäähdytysmoodi aktiivinen, {temp}°C',
    'thermostat.excludeSummerMode': 'Ei kesämoodin alennusta',

    // Ventilation Settings
    'ventilation.title': 'Ilmanvaihdon ohjaus',
    'ventilation.sensors': 'Anturit',
    'ventilation.triggerLevel': 'Mitattu arvo tilalle',
    'ventilation.outdoorTemp': 'Ulkolämpötila (°C)',
    'ventilation.co2Exceeds': 'CO2 ylittää (ppm)',
    'ventilation.humidityExceeds': 'Kosteus ylittää (%)',
    'ventilation.humidityBelow': 'Kosteus alle (%)',
    'ventilation.co2Ppm': 'CO2 (ppm)',
    'ventilation.humidityPercent': 'Kosteus (%)',
    'ventilation.triggerValue': 'Laukaisuarvo',
    'ventilation.switchBackDiff': 'Palautuksen ero',
    'ventilation.summerMode': 'Kesämoodi',
    'ventilation.manualOverrides': 'Manuaaliset ohitukset',
    'ventilation.scheduledOverrides': 'Aikataulutetut ohitukset',
    'ventilation.name': 'Nimi',
    'ventilation.minutes': 'Minuutit',
    'ventilation.activeNow': 'Aktiivinen nyt',
    'ventilation.active': 'Aktiivinen',
    'ventilation.inactive': 'Ei aktiivinen',
    'ventilation.thermometerName': 'Lämpömittarin nimi',
    'ventilation.targetTemp': 'Tavoitelämpötila',
    'ventilation.timeWindow': 'Aikaikkuna',
    'ventilation.outdoorTempDelta': 'Ulkolämpötila alempi asteina',
    'ventilation.noThermometer': 'Ei lämpömittaria valittuna',
    'ventilation.startTime': 'Alkuaika',
    'ventilation.endTime': 'Loppuaika',
    'ventilation.invalidTimeSequence': '⚠️ Virheellinen aikajärjestys yöjäähdytysjaksolla',

    // Consumption Settings
    'consumption.title': 'Kulutuksenohjausasetukset',
    'consumption.actorName': 'Toimijan nimi',
    'consumption.controlSettings': 'Ohjausasetukset',
    'consumption.activePeriod': 'Aktiivinen jakso',
    'consumption.maxCurrent': 'Maksimivirta (A)',
    'consumption.enableHours': 'Käytä matalimman spot-hinnan tuntien määrä',
    'consumption.timeWindow': 'Aikaikkuna',
    'consumption.slots': '15min aikajaksojen määrä',
    'consumption.ranges': 'Vaihteluvälit',
    'consumption.overrideSettings': 'Override-asetukset',
    'consumption.overrideType': 'Override-tyyppi',
    'consumption.noServices': 'Kulutuksenohjausta ei ole määritetty.',
    'consumption.missingMetadata': 'Kulutuksenohjauksen metatiedot puuttuvat.',
    'consumption.notApplicable': 'Ei käytössä',

    // Monthly Status
    'monthly.previousMonth': 'Edellinen kuukausi',
    'monthly.nextMonth': 'Seuraava kuukausi',
    'monthly.noData': 'Kuukausitietoja ei saatavilla',
    'monthly.priceComparison': 'Hintavertailu',
    'monthly.pvSummary': 'Aurinkosähkön yhteenveto - BETA',
    'monthly.heatingEnergy': 'Lämmitysenergian kulutus',
    'monthly.heatingByDevice': 'Kulutus lämmityslaitteittain',
    'monthly.heatingDistribution': 'Jakauma lämmityslaitetyypeittäin',
    'monthly.heatingDeviceBreakdown': 'Energia lämmityslaitteittain',
    'monthly.consumptionDeviceBreakdown': 'Energia kulutusohjatuittain',
    'monthly.dailyEnergyBreakdown': 'Päivittäinen energiajakauma',
    'monthly.energyHeating': 'Lämmitys',
    'monthly.energyConsumption': 'Kulutusohjaus',
    'monthly.energyOther': 'Muu (ohjaamaton)',
    'monthly.totalControlled': 'Ohjattu yhteensä',
    'monthly.totalMetered': 'Mitattu yhteensä',
    'monthly.energySummary': 'Energiayhteenveto',
    'monthly.totalConsumption': 'Kokonaiskulutus',
    'monthly.totalCost': 'Kokonaiskustannus',
    'monthly.deviceCount': 'Laitteita',
    'monthly.devices': 'laitetta',
    'monthly.heatingSummary': 'Lämmityksen yhteenveto',
    'monthly.consumptionSummary': 'Kulutuksen yhteenveto',
    'monthly.consumptionByDevice': 'Kulutus laitteittain (kWh)',
    'monthly.costByDevice': 'Kustannus laitteittain',
    'monthly.costByDeviceEuros': 'Kustannus laitteittain (€)',
    'monthly.otherConsumption': 'Muu energiankulutus',
    'monthly.co2Levels': 'CO2-tasot',
    'monthly.temperatures': 'Lämpötilat',
    'monthly.humidity': 'Kosteus',
    'monthly.recommendedTemps': 'Kodin tilojen suositeltavat lämpötilat vedottomissa olosuhteissa (Motiva)',
    'monthly.recommendedHumidity': 'Kodin tilojen suositeltavat kosteusprosentit:',
    'monthly.bedrooms': 'Makuuhuoneet',
    'monthly.livingSpaces': 'Oleskelutilat',
    'monthly.bathrooms': 'Kylpyhuoneet',
    'monthly.storageRooms': 'Varastot',
    'monthly.garages': 'Autotallit',
    'monthly.currentUtilisation': 'Virran käyttö',
    'monthly.powerTracking': 'Tehon seuranta',

    // Heating Charts
    'heatingChart.title': 'Sähkön hinta, kokonaiskulutus ja kustannus',
    'heatingChart.titleWithPV': 'Sähkön hinta, kokonaiskulutus ja kustannus aurinkosähköllä - BETA',
    'heatingChart.thermostatDetails': 'Termostaattitiedot',
    'heatingChart.consumptionDetails': 'Kulutuksenohjauksen tiedot',
    'heatingChart.totalConsumption': 'Kokonaiskulutus',
    'heatingChart.totalConsumptionBelowAvg': 'Kokonaiskulutus - alle päivän keskiarvon',
    'heatingChart.totalConsumptionAboveAvg': 'Kokonaiskulutus - yli päivän keskiarvon',
    'heatingChart.electricityPrice': 'Sähkön hinta',
    'heatingChart.dayAverage': 'Päivän keskiarvo',
    'heatingChart.totalCost': 'Kokonaiskustannus',
    'heatingChart.spotAvgFull': 'Spot-hinnan keskiarvo (koko 24h)',
    'heatingChart.realisedAvg': 'Toteutunut keskihinta',
    'heatingChart.estimatedSaving': 'Arvioidut säästöt, vain täysille päiville. Säästöt = (Kokonaiskulutus x Spot-keskiarvo) - Kokonaiskustannus',
    'heatingChart.outdoorTempAvg': 'Ulkolämpötilan keskiarvo',
    'heatingChart.avgTemp': 'Keskilämpötila',
    'heatingChart.minTemp': 'Minimilämpötila',
    'heatingChart.maxTemp': 'Maksimilämpötila',
    'heatingChart.targetTemp': 'Tavoitelämpötila',
    'heatingChart.measuredTemp': 'Mitattu lämpötila',
    'heatingChart.consumption': 'Kulutus (kWh)',
    'heatingChart.temperature': 'Lämpötila (°C)',
    'heatingChart.price': 'Hinta/Kustannus (c)',
    'heatingChart.energy': 'Energia (kWh)',
    'heatingChart.chargingHeatRetainer': 'Varaavan laitteen lataus',
    'heatingChart.ahpTargetTemp': 'Ilmalämpöpumpun tavoitelämpötila',
    'heatingChart.calibrationValue': 'Kalibrointiarvo',
    'heatingChart.fanSpeed': 'Puhaltimen nopeus',
    'heatingChart.outdoorTemp': 'Ulkolämpötila',
    'heatingChart.settingsAt': 'Asetukset',
    'heatingChart.ahpSettings': 'Ilmalämpöpumpun asetukset',
    'heatingChart.ahpCalibration': 'Ilmalämpöpumpun kalibrointi',
    'heatingChart.ahpFanSpeed': 'Ilmalämpöpumpun puhaltimen nopeus',
    'heatingChart.configProfile': 'Ohjausprofiili',
    'heatingChart.output': 'Lähtö',
    'heatingChart.override': 'Käsiohitus',
    'heatingChart.pvOutput': 'Aurinkosähkön ohjaus',
    'heatingChart.hourlyConsumption': 'Tuntikulutus',
    'heatingChart.consumptionBelowAboveAvg': 'Kulutus alle/yli keskiarvon',
    'heatingChart.gridEnergy': 'Verkosta otettu / Verkkoon syötetty energia',
    'heatingChart.solarProduction': 'Aurinkosähkön tuotanto',
    'heatingChart.gridEnergyBelowAboveAvg': 'Verkkoenergia alle/yli keskiarvon',
    'heatingChart.totalIncome': 'Kokonaistulot aurinkosähkön myynnistä',
    'heatingChart.costOfReceived': 'Verkkoenergiän kustannus',
    'heatingChart.netCost': 'Nettokustannus',
    'heatingChart.gridConsumption': 'Verkkokulutus',
    'heatingChart.solarConsumption': 'Aurinkosähkökulutus',
    'heatingChart.energySold': 'Myyty energia',
    'heatingChart.solarSummary': 'Aurinkosähkön yhteenveto',
    'heatingChart.solarOptimizationTitle': 'Aurinkosähkön optimointi - Laitteiden ohjaus',
    'heatingChart.panelPower': 'Paneelien teho',
    'heatingChart.gridPower': 'Verkkoteho (+ tuonti, - vienti)',
    'heatingChart.deviceCapacity': 'Laitteiden kapasiteetti',
    'heatingChart.actualPanelConsumption': 'Toteutunut paneelien kulutus',
    'heatingChart.optimizationSummary': 'Optimoinnin yhteenveto',
    'heatingChart.producedSolarPower': 'Tuotettu aurinkosähkö',
    'heatingChart.consumedSolarPower': 'Kulutettu aurinkosähkö',
    'heatingChart.exportSoldPower': 'Viety / Myyty energia',
    'heatingChart.peakSolarProduction': 'Aurinkosähkön huipputuotanto',
    'heatingChart.totalMeteredConsumption': 'Mitattu kokonaiskulutus',
    'heatingChart.habitaioSmartControl': 'Habitaio Älyohjaus:',
    'heatingChart.phaseCurrents': 'Vaiheiden virrat (L1, L2, L3)',
    'heatingChart.totalCurrent': 'Kokonaisvirta',
    'heatingChart.current': 'Virta',
    'heatingChart.phase1Current': 'Vaihe 1 (L1)',
    'heatingChart.phase2Current': 'Vaihe 2 (L2)',
    'heatingChart.phase3Current': 'Vaihe 3 (L3)',
    'heatingChart.reference': 'sulake',
    'heatingChart.phase1Max': 'Vaihe 1 Max',
    'heatingChart.phase2Max': 'Vaihe 2 Max',
    'heatingChart.phase3Max': 'Vaihe 3 Max',
    'heatingChart.phase1Avg': 'Vaihe 1 Keskiarvo',
    'heatingChart.phase2Avg': 'Vaihe 2 Keskiarvo',
    'heatingChart.phase3Avg': 'Vaihe 3 Keskiarvo',
    'heatingChart.totalCurrentMax': 'Kokonaisvirran maksimi',
    'heatingChart.totalCurrentAvg': 'Kokonaisvirran keskiarvo',
    'heatingChart.phaseCurrentStats': 'Vaiheiden virtatilastot',
    'heatingChart.totalCurrentStats': 'Kokonaisvirran tilastot',
    'heatingChart.meteredTotal': 'Kokonaiskulutus (mitattu)',
    'heatingChart.estimatedPhaseCurrent': 'Arvioitu vaihevirtojen keskiarvo',
    'heatingChart.estimatedPhaseCurrentMax': 'Arvioitu vaihevirran maksimi',
    'heatingChart.estimatedPhaseCurrentAvg': 'Arvioitu vaihekeskiarvovirran keskiarvo',
    'heatingChart.power': 'Teho',
    'heatingChart.powerMax': 'Tehon maksimi',
    'heatingChart.powerAvg': 'Tehon keskiarvo',
    'heatingChart.powerMin': 'Tehon minimi',
    'heatingChart.powerConsumption': 'Tehonkulutus',
    'heatingChart.powerStats': 'Tehon tilastot',
    'heatingChart.showHourlyView': 'Näytä tuntinäkymä',
    'heatingChart.show15minDetails': 'Näytä 15 min tarkkuus',

    // Ventilation Charts
    'ventChart.status': 'Tilanne',
    'ventChart.history': 'Historia',
    'ventChart.powerChart': 'Tehokuvaaja',
    'ventChart.timeOn': 'Päällä',
    'ventChart.state': 'Tila',
    'ventChart.threshold': 'Raja-arvo',

    // Days of week (short form)
    'days.mon': 'Ma',
    'days.tue': 'Ti',
    'days.wed': 'Ke',
    'days.thu': 'To',
    'days.fri': 'Pe',
    'days.sat': 'La',
    'days.sun': 'Su',

    // Days of week (full form)
    'days.monday': 'Maanantai',
    'days.tuesday': 'Tiistai',
    'days.wednesday': 'Keskiviikko',
    'days.thursday': 'Torstai',
    'days.friday': 'Perjantai',
    'days.saturday': 'Lauantai',
    'days.sunday': 'Sunnuntai',

    // Backend config values - Reference prices
    'backend.dayAverage': 'Päivän keskiarvo',
    'backend.spotAverage': 'Spot-keskiarvo',
    'backend.below33': 'Alle 33%',
    'backend.below50': 'Alle 50%',

    // Backend config values - Common adjustment names
    'backend.nightReduction': 'Yöalennus',
    'backend.dayReduction': 'Päiväalennus',
    'backend.weekendReduction': 'Viikonloppualennus',
    'backend.morningBoost': 'Aamukorotus',
    'backend.eveningBoost': 'Iltakorotus',

    // Thermostat Wizard
    'wizard.createThermostat': 'Luo termostaatti',
    'wizard.addThermostat': 'Lisää termostaatti',
    'wizard.addToExisting': 'Lisää olemassa olevaan profiiliin',
    'wizard.createNewProfile': 'Luo uusi profiili',
    'wizard.selectProfile': 'Valitse profiili',
    'wizard.selectTemplate': 'Valitse malli',
    'wizard.profileName': 'Profiilin nimi',
    'wizard.thermostatName': 'Termostaatin nimi',
    'wizard.thermostatType': 'Tyyppi',
    'wizard.location': 'Sijainti',
    'wizard.selectSensor': 'Lämpömittari',
    'wizard.selectHeaters': 'Lämmittimet',
    'wizard.advancedSettings': 'Lisäasetukset',
    'wizard.back': 'Takaisin',
    'wizard.next': 'Seuraava',
    'wizard.create': 'Luo',
    'wizard.step': 'Vaihe',
    'wizard.stepProfile': 'Profiili',
    'wizard.stepDetails': 'Tiedot',
    'wizard.stepAdvanced': 'Lisäasetukset',
    'wizard.required': 'Pakollinen',
    'wizard.optional': 'Valinnainen',
    'wizard.selectOption': 'Valitse',
    'wizard.deviceSpecificSettings': 'Laitekohtaiset asetukset',
    'wizard.advancedSettingsHelp': 'Nämä asetukset ovat valinnaisia ja niillä on oletusarvot.',
    'wizard.noAdvancedSettings': 'Ei lisäasetuksia saatavilla.',
    'wizard.saving': 'Luodaan...',
    'wizard.saveSuccess': 'Termostaatti luotu onnistuneesti!',
    'wizard.saveError': 'Termostaatin luonti epäonnistui. Yritä uudelleen.',
  }
};

// Simple translation function
// Second arg: string = fallback, object = interpolation params
export const t = (key, fallbackOrParams = key) => {
  const raw = translations[currentLang]?.[key];
  if (typeof fallbackOrParams === 'object' && fallbackOrParams !== null) {
    const template = raw || key;
    return template.replace(/\{(\w+)\}/g, (_, k) =>
      fallbackOrParams[k] !== undefined ? fallbackOrParams[k] : `{${k}}`
    );
  }
  return raw || fallbackOrParams;
};

// Helper to get current language
export const getCurrentLanguage = () => currentLang;

// Helper to set language dynamically
export const setCurrentLanguage = (lang) => {
  if (translations[lang]) {
    currentLang = lang;
    console.log(`Language set to: ${lang}`);
    return true;
  } else {
    console.warn(`Language '${lang}' not supported. Available: ${Object.keys(translations).join(', ')}`);
    return false;
  }
};

// Backward compatible function for thermostat settings labels
export const getSettingLabel = (key) => {
  // Map snake_case setting keys to translation keys
  const keyMap = {
    'dynamic_calibration_enabled': 'thermostat.dynamicCalibration',
    'fan_control_enabled': 'thermostat.fanControl',
    'calibration_value': 'thermostat.calibrationValue',
    'adjustment_value': 'thermostat.adjustmentValue',
    'override_enabled': 'thermostat.overrideEnabled',
    'override_minutes': 'thermostat.overrideMinutes',
  };

  const labelKey = keyMap[key];
  return labelKey ? t(labelKey) : key;
};

// Helper to translate backend config values (like "Day Average", "Monday", etc.)
export const translateBackendValue = (value) => {
  if (!value || typeof value !== 'string') return value;

  // Create a normalized key from the value (remove spaces, convert to camelCase)
  const normalizeKey = (str) => {
    return str
      .toLowerCase()
      .replace(/[^\w\s]/g, '') // Remove special chars
      .replace(/\s+(.)/g, (_, char) => char.toUpperCase()) // camelCase
      .replace(/\s/g, ''); // Remove remaining spaces
  };

  // Common mappings for backend config values
  const valueMap = {
    // Reference prices
    'Day Average': 'backend.dayAverage',
    'Spot Average': 'backend.spotAverage',
    'Below 33%': 'backend.below33',
    'Below 50%': 'backend.below50',

    // Days (full form)
    'Monday': 'days.monday',
    'Tuesday': 'days.tuesday',
    'Wednesday': 'days.wednesday',
    'Thursday': 'days.thursday',
    'Friday': 'days.friday',
    'Saturday': 'days.saturday',
    'Sunday': 'days.sunday',

    // Common adjustment names
    'Night reduction': 'backend.nightReduction',
    'Day reduction': 'backend.dayReduction',
    'Morning boost': 'backend.morningBoost',
    'Evening boost': 'backend.eveningBoost',
  };

  // Try exact match first
  if (valueMap[value]) {
    return t(valueMap[value]);
  }

  // Try normalized key
  const normalizedKey = normalizeKey(value);
  const backendKey = `backend.${normalizedKey}`;
  const translated = t(backendKey, null);
  if (translated) return translated;

  // Try days key
  const daysKey = `days.${normalizedKey}`;
  const dayTranslated = t(daysKey, null);
  if (dayTranslated) return dayTranslated;

  // Return original value if no translation found
  return value;
};

// Helper specifically for day names in active_days objects
export const translateDayName = (dayName) => {
  const dayMap = {
    'Monday': 'days.monday',
    'Tuesday': 'days.tuesday',
    'Wednesday': 'days.wednesday',
    'Thursday': 'days.thursday',
    'Friday': 'days.friday',
    'Saturday': 'days.saturday',
    'Sunday': 'days.sunday',
  };

  return t(dayMap[dayName] || `days.${dayName.toLowerCase()}`, dayName);
};

const i18n = { t, getCurrentLanguage, setCurrentLanguage, getSettingLabel, translateBackendValue, translateDayName };
export default i18n;
